import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import {
  KentRebalancingPlan,
  KentRebalancingPlanStatusEnum,
} from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const KENT_REBALANCE_PLAN_QUERY_KEY = 'kent-rebalance-plan';

export const getKentRebalancePlanQueryKey = (
  clusterId?: string,
  rebalancePlanId?: string
) => [KENT_REBALANCE_PLAN_QUERY_KEY, clusterId, rebalancePlanId] as const;

type KentRebalancePlansQueryOptionsParams = {
  clusterId?: string;
  organizationId?: string;
  limit?: number;
  includeStatuses?: KentRebalancingPlanStatusEnum[];
  enabled?: boolean;
};

const getKentRebalancePlansQueryKey = ({
  clusterId,
  limit = 20,
  includeStatuses,
}: Omit<
  KentRebalancePlansQueryOptionsParams,
  'organizationId' | 'enabled'
> = {}) =>
  [KENT_REBALANCE_PLAN_QUERY_KEY, clusterId, limit, includeStatuses] as const;

export const getKentRebalancePlansQueryOptions = ({
  clusterId,
  organizationId,
  limit = 20,
  includeStatuses,
  enabled,
}: KentRebalancePlansQueryOptionsParams = {}) =>
  infiniteQueryOptions({
    queryKey: getKentRebalancePlansQueryKey({
      clusterId,
      limit,
      includeStatuses,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.kent.listRebalancingPlans({
        organizationId: organizationId ?? '',
        clusterId: clusterId!,
        pageLimit: String(limit),
        pageCursor: pageParam,
        statusFilter: includeStatuses,
      });
      return data;
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextPageCursor || undefined,
    enabled: !!clusterId && !!organizationId && enabled !== false,
    refetchInterval: 20000,
  });

type UseKentRebalancePlansQuery = {
  isLoading: boolean;
  rebalancePlans?: KentRebalancingPlan[];
  activeRebalancingPlanId?: string;
  error?: unknown;
  refetch?: () => void;
  fetchNextPage?: () => void;
  isFetching?: boolean;
  hasNextPage?: boolean;
};

type UseKentRebalancePlansQueryProps = {
  clusterId?: string;
  disabled?: boolean;
  limit?: number;
  includeStatuses?: KentRebalancingPlanStatusEnum[];
};

export const useKentRebalancePlansQuery = ({
  clusterId,
  disabled,
  limit = 20,
  includeStatuses,
}: UseKentRebalancePlansQueryProps): UseKentRebalancePlansQuery => {
  const { currentOrganization } = useOrganizations();

  const {
    isLoading,
    data,
    error,
    refetch,
    fetchNextPage,
    isFetching,
    hasNextPage,
  } = useInfiniteQuery({
    ...getKentRebalancePlansQueryOptions({
      clusterId,
      organizationId: currentOrganization?.id,
      limit,
      includeStatuses,
      enabled: !disabled,
    }),
    select: (data) => data.pages.flatMap((page) => page.plans ?? []),
  });

  return {
    isLoading,
    rebalancePlans: data,
    error,
    refetch,
    fetchNextPage,
    isFetching,
    hasNextPage,
  };
};
